import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { useProvisaoRecursosFilters } from "../hooks/Qualidade_provisao_recursos/filter";
import { useProvisaoRecursosPagination } from "../hooks/Qualidade_provisao_recursos/pagination";
import { QualidadeProvisaoRecursoForm, QualidadeProvisaoRecursoSchema, ReportProvisaoRecursoForm, ReportProvisaoRecursoSchema } from "../schemas/ProvisaoRecurso/qualidade_provisao_recurso";
import { ProvisaoRecursoLista } from "../../domain/models/provisao_recurso_lista";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

type LoadProvisaoRecursosListaListaFuncState = {
  IdProvisaoRecurso?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  status?: number,
  pesquisa?: string | number | Date,
};

export const useQualidadeProvisaoRecursosListaViewModel = () => {
  const {
    commons: { getAllLogAlteracaoUseCase, getAllLogAlteracaoByParentUseCase },
    reports: { getReportProvisaoRecursosUseCase },
    qualidade: { getAllListagemProvisaoRecursoUseCase, deleteProvisaoRecursoUseCase, createProvisaoRecursoUseCase, updateProvisaoRecursoUseCase }

  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
  } = useDropDownLoader();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoByParentUseCase: getAllLogAlteracaoByParentUseCase,
    getAllLogAlteracaoUseCase: getAllLogAlteracaoUseCase,
    onSetError: (e) => pushErrorNotification("Erro", [e]),
  });

  const hookForm = useForm<ReportProvisaoRecursoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ReportProvisaoRecursoSchema),
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [provisaoRecursosModal, setProvisaoRecursosModal] = useState<boolean>(false);
  const [provisaoRecursosLista, setProvisaoRecursosLista] = useState<ProvisaoRecursoLista[]>([])
  const [selectedProvisaoRecursos, setSelectedProvisaoRecursos] = useState<ProvisaoRecursoLista>();
  const [centroDeCustoLista, setCentroDeCustoLista] = useState<CentroCustoDropDown[]>([])
  const [provisaoRecursosDeleteModal, setProvisaoRecursosDeleteModal] = useState<boolean>(false);

  const { pushErrorNotification } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(750);

  const filters = useProvisaoRecursosFilters();
  const pagination = useProvisaoRecursosPagination();

  //#region Relatorio
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportViewerState, startReportViewerProcess] = useReportViewer();
  //#endregion


  // #region Funções de carregamento

  const loadProvisaoRecursosListaState = useCallback(async (funcState?: LoadProvisaoRecursosListaListaFuncState) => {
    try {
      const filterID_ProvisaoRecurso = evaluateParamValue(funcState?.IdProvisaoRecurso, filters.IdProvisaoRecurso);
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
      await getAllListagemProvisaoRecursoUseCase.getAllListagemProvisaoRecurso(
        {
          IdProvisaoRecurso: filterID_ProvisaoRecurso, PesquisarPor: filteredTipoPesquisa, Pesquisar: String(filteredPesquisa)
        },
        filteredPage,
        filteredRowsPerPage
      );
      setProvisaoRecursosLista(getAllListagemProvisaoRecursoUseCase.items);
      pagination.setRowsPerPage(getAllListagemProvisaoRecursoUseCase.pageSize);
      pagination.setTotalItems(getAllListagemProvisaoRecursoUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao Provisao Recursos Lista!");
    }
  }, [filters.IdProvisaoRecurso, filters.pesquisa, filters.tipoPesquisa, getAllListagemProvisaoRecursoUseCase, pagination]);


  //#endregion

  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const jobs = [];
      await loadCentroCustoDropDown(list => setCentroDeCustoLista(list));
      jobs.push(loadProvisaoRecursosListaState());
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      jobs.push(loadProvisaoRecursosListaState());

      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProvisaoRecursosListaState, pushErrorNotification]);
  //#endregion

  // #region Eventos

  const handleProvisaoRecursosSubmit = useCallback(async (form: QualidadeProvisaoRecursoForm) => {
    try {
      if (selectedProvisaoRecursos) {
        await updateProvisaoRecursoUseCase.updateProvisaoRecurso(selectedProvisaoRecursos.IdProvisaoRecurso, form.Recurso, form.Observacao ?? null, form.DataPrevistaDe ?? null, form.DataPrevistaAte ?? null, form.DataRealizadaDe ?? null, form.DataRealizadaAte ?? null, form.ValorPrevisto ?? null, form.ValorDesembolsado ?? null);
        setProvisaoRecursosModal(false);
      } else {
        await createProvisaoRecursoUseCase.createProvisaoRecurso(form.Recurso, form.Observacao ?? null, form.DataPrevistaDe ?? null, form.DataPrevistaAte ?? null, form.DataRealizadaDe ?? null, form.DataRealizadaAte ?? null, form.ValorPrevisto ?? null, form.ValorDesembolsado ?? null)
        setProvisaoRecursosModal(false);
      }
      await loadProvisaoRecursosListaState();
    } catch (e) {
      pushErrorNotification("Erro", processError(e, 'Falha ao criar Provisao Recurso!'));
    } finally {
      setSaving(false);
    }
  }, [createProvisaoRecursoUseCase, loadProvisaoRecursosListaState, pushErrorNotification, selectedProvisaoRecursos, updateProvisaoRecursoUseCase]);

  const handleCadastroProvisaoRecursosOpenState = useCallback((IdProvisaoRecurso: number | null, openState: boolean) => {
    if (openState) {
      if (IdProvisaoRecurso) {
        setSelectedProvisaoRecursos(provisaoRecursosLista.find(x => x.IdProvisaoRecurso === IdProvisaoRecurso));
      } else {
        setSelectedProvisaoRecursos(undefined);
      }
    }
    setProvisaoRecursosModal(openState);
  }, [provisaoRecursosLista]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadProvisaoRecursosListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProvisaoRecursosListaState, pagination, pushErrorNotification]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadProvisaoRecursosListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadProvisaoRecursosListaState, pagination, pushErrorNotification]);

  const handleDeleteProvisaoRecursosModalOpenState = useCallback((IdProvisaoRecurso: number | null, openState: boolean) => {
    if (openState) {
      if (IdProvisaoRecurso && provisaoRecursosLista) {
        setSelectedProvisaoRecursos(provisaoRecursosLista.find(x => x.IdProvisaoRecurso === IdProvisaoRecurso));
      } else {
        setSelectedProvisaoRecursos(undefined);
      }
    }
    setProvisaoRecursosDeleteModal(openState);
  }, [provisaoRecursosLista]);


  const handleDeleteConfirmaProvisaoRecursos = useCallback(async () => {
    if (selectedProvisaoRecursos) {
      try {
        setSaving(true);
        await deleteProvisaoRecursoUseCase.deleteProvisaoRecurso(selectedProvisaoRecursos.IdProvisaoRecurso);
        await loadProvisaoRecursosListaState();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setProvisaoRecursosDeleteModal(false);
      }
    }
  }, [deleteProvisaoRecursoUseCase, loadProvisaoRecursosListaState, pushErrorNotification, selectedProvisaoRecursos]);

  //#endregion

  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadProvisaoRecursosListaState();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar Provisao Recursos!'));
    } finally {
      setLoading(false);
    }
  }, [filters, loadProvisaoRecursosListaState, pagination, pushErrorNotification]);

  const handlePesquisaChange = useDebouncedCallback(debouncedRef, (newValue: string | Date | number | null) => {
    useProvisaoRecursosFilters.getState().setPesquisa(newValue);

    if (newValue) {
      return async () => {
        try {
          setLoading(true);
          useProvisaoRecursosPagination.getState().setPage(1);
          await loadProvisaoRecursosListaState({ pesquisa: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar Provisao Recursos!"));
        } finally {
          setLoading(false);
        }
      }
    } else {
      return async () => {
        try {
          setLoading(true);
          useProvisaoRecursosPagination.getState().setPage(1);
          await loadProvisaoRecursosListaState();
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao carregar Provisao Recursos!"));
        } finally {
          setLoading(false);
        }
      }
    }
  },
    [loadProvisaoRecursosListaState, pushErrorNotification, setLoading]
  );

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useProvisaoRecursosFilters.getState().setTipoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useProvisaoRecursosPagination.getState().setPage(1);
        await loadProvisaoRecursosListaState({ tipoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar Provisao Recurso!"));
      } finally {
        setLoading(false);
      }
    }
  }, [loadProvisaoRecursosListaState, pushErrorNotification]);

  //#endregion

  //#region  Relatorio
  const handleReportModalOpenState = useCallback((openState: boolean) => {
    setOpenReportModal(openState);
  }, []);

  const handleSubmitReport = useCallback(async (form: ReportProvisaoRecursoForm) => {
    try {
      setLoading(true);
      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportProvisaoRecursosUseCase.getReportProvisaoRecursos(
            requiredMimeType,
            form.DataPrevista ?? null,
            form.DataRealizada ?? null,
          ),
        closeReportModalCallback: () => {
          setOpenReportModal(false);
        }
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
    } finally {
      setLoading(false);
    }
  }, [getReportProvisaoRecursosUseCase, pushErrorNotification, startReportViewerProcess]);

  //#endregion
  const handleOpenChangeHistory = useCallback(
    (idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
      if (idTabela && tipoTabela) {
        changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
      }
    },
    [changeHistory],
  );


  return {
    loading,
    saving,
    errorMessage,
    pagination,
    filters,
    provisaoRecursosLista,
    provisaoRecursosDeleteModal,
    selectedProvisaoRecursos,
    provisaoRecursosModal,
    centroDeCustoLista,
    openReportModal,
    reportViewerState,
    hookForm,
    changeHistory,
    handleReload,
    handleOpenChangeHistory,
    handleRowsPerPageChange,
    handlePageChange,
    handleTipoPesquisaChange,
    handlePesquisaChange,
    handleClearFilters,
    handleDeleteProvisaoRecursosModalOpenState,
    handleDeleteConfirmaProvisaoRecursos,
    handleCadastroProvisaoRecursosOpenState,
    handleProvisaoRecursosSubmit,
    handleReportModalOpenState,
    handleSubmitReport,
  }
}